/**
 * TraceLesson
 * -----------
 * Audio-first "teach to write" step for letters and numbers.
 *
 *  1. Shows the character as a big dashed SVG outline with an animated red
 *     "pencil tip" that traces the correct stroke-order path.
 *  2. The child can then trace the SAME character with their finger / stylus
 *     on a canvas layered right on top of the outline.
 *  3. A "✅ I did it!" button records the attempt. "↺ Try again" clears the
 *     canvas and plays the stroke animation one more time.
 *
 *  No external deps — pure React + Canvas 2D + Web Speech for narration.
 *  Props:
 *    char            — "A" | "a" | "5" | ...
 *    strokePath      — SVG path "d" string describing the correct stroke order.
 *                      Multiple strokes can be supplied as an array of strings.
 *    narration       — optional text read aloud when the lesson starts.
 *    onDone          — called when the child taps "I did it!".
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, RotateCcw, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';

const CANVAS_SIZE = 320;

const speakBrowser = (text) => {
  try {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return null;
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = 0.9;
    u.pitch = 1.05;
    u.volume = 1;
    window.speechSynthesis.speak(u);
    return u;
  } catch {
    return null;
  }
};

export const TraceLesson = ({ char, strokePath, narration, onDone }) => {
  const strokes = useMemo(
    () => (Array.isArray(strokePath) ? strokePath : [strokePath]).filter(Boolean),
    [strokePath]
  );
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [attempts, setAttempts] = useState(0);

  // Narrate once on mount.
  useEffect(() => {
    if (narration) speakBrowser(narration);
    return () => {
      try { window.speechSynthesis?.cancel(); } catch {}
    };
    // eslint-disable-next-line
  }, []);

  const getCtx = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0F766E';
    ctx.lineWidth = 10;
    return ctx;
  };

  const localCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = localCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
    setHasDrawn(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = getCtx();
    const { x, y } = localCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => setDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const replayAnimation = () => {
    clearCanvas();
    setAnimationKey((k) => k + 1);
    if (narration) speakBrowser(narration);
  };

  const handleDone = () => {
    setAttempts((n) => n + 1);
    speakBrowser("Nice work tracing!");
    onDone && onDone({ char, attempts: attempts + 1 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
      data-testid={`trace-lesson-${char}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-bold text-slate-800">Trace the letter <span className="text-teal-600">{char}</span></h3>
        <button
          type="button"
          onClick={() => narration && speakBrowser(narration)}
          className="p-2 rounded-full bg-teal-50 text-teal-700 hover:bg-teal-100"
          data-testid="trace-replay-narration"
          aria-label="Replay narration"
        >
          <Volume2 size={18} />
        </button>
      </div>

      {/* Stroke-order animation (SVG) layered under the trace canvas. */}
      <div
        className="relative mx-auto"
        style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
      >
        <svg
          key={animationKey}
          viewBox={`0 0 ${CANVAS_SIZE} ${CANVAS_SIZE}`}
          className="absolute inset-0 pointer-events-none"
          data-testid="trace-stroke-guide"
        >
          {/* Dotted light-gray guide: the full letter outline, always visible. */}
          {strokes.map((d, i) => (
            <path
              key={`guide-${i}`}
              d={d}
              fill="none"
              stroke="#CBD5E1"
              strokeWidth={30}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.55}
            />
          ))}
          {/* Orange "pencil" dashes animating along each stroke to show order. */}
          {strokes.map((d, i) => (
            <path
              key={`anim-${i}`}
              d={d}
              fill="none"
              stroke="#F97316"
              strokeWidth={6}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="14 12"
              style={{
                animation: `trace-dash 1.8s linear ${i * 1.4}s infinite`,
              }}
            />
          ))}
          {/* Start dot markers so kids know where to begin each stroke. */}
          {strokes.map((d, i) => {
            // Extract the first "M x y" of each stroke for the start marker.
            const m = /M\s*([0-9.]+)\s+([0-9.]+)/.exec(d);
            if (!m) return null;
            return (
              <g key={`start-${i}`}>
                <circle cx={m[1]} cy={m[2]} r={10} fill="#059669" />
                <text
                  x={m[1]}
                  y={m[2] + 5}
                  fill="white"
                  fontSize="12"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  {i + 1}
                </text>
              </g>
            );
          })}
        </svg>

        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          className="absolute inset-0 touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="trace-canvas"
        />
      </div>

      <div className="mt-5 flex items-center justify-center gap-3">
        <Button
          variant="outline"
          onClick={replayAnimation}
          data-testid="trace-reset-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Try again
        </Button>
        <Button
          onClick={handleDone}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={!hasDrawn}
          data-testid="trace-done-btn"
        >
          <Check className="w-4 h-4 mr-2" /> I did it!
        </Button>
      </div>

      <style>{`
        @keyframes trace-dash {
          0% { stroke-dashoffset: 110; }
          100% { stroke-dashoffset: 0; }
        }
      `}</style>
    </motion.div>
  );
};

export default TraceLesson;
